import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import { money, pickLang, t } from "@/i18n/booking";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const today = () => new Date().toISOString().slice(0, 10);

function ContactForm({ lang }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [state, setState] = useState({ sending: false, sent: false, error: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const send = async (e) => {
    e.preventDefault();
    setState({ sending: true, sent: false, error: "" });
    try {
      await api.post("/public/contact", form);
      setState({ sending: false, sent: true, error: "" });
    } catch (err) {
      setState({ sending: false, sent: false, error: formatError(err) });
    }
  };
  if (state.sent) return <p className="text-marca" data-testid="contact-sent">{t(lang, "contact_sent")}</p>;
  return (
    <form onSubmit={send} className="grid sm:grid-cols-3 gap-3" data-testid="contact-form">
      <Input required placeholder={t(lang, "name")} value={form.name} onChange={set("name")} />
      <Input required type="email" placeholder={t(lang, "email")} value={form.email} onChange={set("email")} />
      <Input placeholder={t(lang, "phone")} value={form.phone} onChange={set("phone")} />
      <Textarea required className="sm:col-span-3" placeholder={t(lang, "contact_message")} value={form.message} onChange={set("message")} />
      {state.error && <p className="sm:col-span-3 text-sm text-acento">{state.error}</p>}
      <Button type="submit" disabled={state.sending} className="bg-marca hover:bg-marca-hover text-white w-fit">{t(lang, "contact_send")}</Button>
    </form>
  );
}

export default function BookingHome({ embedded = false }) {
  const location = useLocation();
  const lang = pickLang(location.search);
  const params = new URLSearchParams(location.search);
  const [vehicles, setVehicles] = useState(null);
  const [dates, setDates] = useState({ start: params.get("start") || "", end: params.get("end") || "" });
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const prefix = embedded ? "/embed/reservar" : "/reservar";

  useEffect(() => {
    api.get("/public/vehicles").then(({ data }) => setVehicles(data.vehicles || [])).catch(() => setVehicles([]));
  }, []);

  const search = async (e) => {
    e?.preventDefault();
    setError("");
    if (!dates.start || !dates.end || dates.end <= dates.start) return setError(t(lang, "error_dates"));
    try {
      const { data } = await api.get("/public/search", { params: dates });
      setResults(Object.fromEntries(data.results.map((r) => [r.vehicle.slug, r])));
    } catch (err) {
      setError(formatError(err));
    }
  };

  return (
    <PublicLayout lang={lang} embedded={embedded}>
      {() => (
        <div className="space-y-8">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A]">{t(lang, "vehicles_title")}</h1>
            <form onSubmit={search} className="mt-4 flex flex-wrap items-end gap-3" data-testid="home-search">
              <label className="text-sm text-[#5C5C5C]">{t(lang, "pickup")}
                <Input type="date" min={today()} value={dates.start} onChange={(e) => setDates((d) => ({ ...d, start: e.target.value }))} className="mt-1 bg-white" data-testid="home-start" />
              </label>
              <label className="text-sm text-[#5C5C5C]">{t(lang, "return")}
                <Input type="date" min={dates.start || today()} value={dates.end} onChange={(e) => setDates((d) => ({ ...d, end: e.target.value }))} className="mt-1 bg-white" data-testid="home-end" />
              </label>
              <Button type="submit" className="bg-marca hover:bg-marca-hover text-white" data-testid="home-search-btn">{t(lang, "search")}</Button>
            </form>
            {error && <p className="mt-2 text-sm text-acento">{error}</p>}
          </div>

          {vehicles && vehicles.length === 0 && <p className="text-[#5C5C5C]">{t(lang, "no_vehicles")}</p>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(vehicles || []).map((v) => {
              const r = results?.[v.slug];
              const qs = new URLSearchParams({ lang, ...(dates.start && dates.end ? dates : {}) }).toString();
              return (
                <div key={v.id} className="bg-white border border-[#DCD8D0] rounded-xl overflow-hidden flex flex-col" data-testid={`home-vehicle-${v.slug}`}>
                  {v.photos?.[0] ? <img src={v.photos[0]} alt={v.name} className="h-44 w-full object-cover" /> : <div className="h-44 bg-[#EAE7E0]" />}
                  <div className="p-4 flex-1 flex flex-col gap-2">
                    <div className="font-display text-lg font-semibold text-[#1A1A1A]">{v.name}</div>
                    <div className="text-sm text-[#5C5C5C]">{t(lang, "seats", { n: v.seats })} · {t(lang, "beds", { n: v.beds })}</div>
                    <div className="text-sm text-[#1A1A1A]">{t(lang, "from_price", { price: v.base_price_per_night })}</div>
                    {r && (
                      <div className={`text-sm font-medium ${r.free ? "text-marca" : "text-acento"}`} data-testid={`home-avail-${v.slug}`}>
                        {r.free ? t(lang, "free_dates") : t(lang, "busy_dates")}
                        {r.free && r.quote ? ` · ${money(lang, r.quote.total)}` : ""}
                        {r.free && r.error ? ` · ${r.error}` : ""}
                      </div>
                    )}
                    <Link to={`${prefix}/${v.slug}?${qs}`} className="mt-auto inline-flex justify-center rounded-md bg-acento hover:bg-acento-hover text-white px-4 py-2 text-sm" data-testid={`home-book-${v.slug}`}>
                      {t(lang, "book")}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {!embedded && (
            <section className="bg-white border border-[#DCD8D0] rounded-xl p-6">
              <h2 className="font-display text-lg font-semibold text-[#1A1A1A] mb-3">{t(lang, "contact_title")}</h2>
              <ContactForm lang={lang} />
            </section>
          )}
        </div>
      )}
    </PublicLayout>
  );
}
